import { type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useBuildsTableController } from '@/features/builds/components/useBuildsTableController';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { formatStatusLabel } from '@/ui';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import type { BuildsTableProps } from '@/features/builds/components/BuildsTable.types';
import type { BuildDto } from '@flowqa/shared';

export const BuildsTable = ({
  fixedFilters,
  noRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'job' }),
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = true,
  disableColumnMenu = true,
  showToolbar = true,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  initialData,
  onRowsChange,
  onQueryMeta,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  nameColumnMode = 'pipeline',
  nameColumnHeaderName,
  showStageSummaryColumn = true,
  showJobColumn = false,
  showPipelineColumn = true,
  search,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  tagOptionsOverride,
  runHrefBuilder,
}: BuildsTableProps): ReactElement => {
  const {
    columns,
    deleteBuildPending,
    deleteTarget,
    editEditor,
    editTarget,
    editTargetDurationMs,
    editTargetStartedAt,
    gridConfig,
    handleCloseDeleteDialog,
    handleCloseEditDialog,
    handleConfirmDelete,
    handleSaveEditDialog,
    isAuthenticated,
    queryFn: resolvedQueryFn,
    queryKey: resolvedQueryKey,
    selectedOrganisationId,
    selectedProjectId,
    togglableColumnFields,
  } = useBuildsTableController({
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    nameColumnMode,
    nameColumnHeaderName,
    persistKey,
    queryFn,
    queryKey,
    runHrefBuilder,
    showJobColumn,
    showPipelineColumn,
    showStageSummaryColumn,
    syncStateToUrl,
    tagOptionsOverride,
  });

  return (
    <>
      <ServerDataTable<BuildDto>
        queryPath="pipeline-runs/query"
        {...gridConfig}
        initialData={initialData}
        queryFn={resolvedQueryFn}
        queryKey={resolvedQueryKey}
        columns={columns}
        getRowId={(row) => row.id}
        isAuthenticated={isAuthenticated}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={pageSizeOptions}
        initialPageSize={initialPageSize}
        search={search}
        columnVisibilityModel={columnVisibilityModel}
        disablePageSizeSelect={disablePageSizeSelect}
        hideFooter={hideFooter}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        showToolbar={showToolbar}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        noRowsText={noRowsText}
        interactionMode="static"
        getRowHeight={() => 'auto'}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={null}
        showLastUpdated={showLastUpdated}
        autoHeight
        onRowsChange={onRowsChange}
        onQueryMeta={onQueryMeta}
        onQueryInput={onQueryInput}
        onDataUpdatedAt={onDataUpdatedAt}
        gridProps={{
          getEstimatedRowHeight: () => TABLE_ROW_MEASURE_HEIGHT,
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        sx={[
          ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          {
            '& .MuiDataGrid-row': {
              minHeight: `${TABLE_ROW_MEASURE_HEIGHT}px !important`,
            },
            '& .MuiDataGrid-cell': {
              minHeight: `${TABLE_ROW_MEASURE_HEIGHT}px !important`,
            },
            '& .MuiDataGrid-cell[data-field="buildKey"], & .MuiDataGrid-cell[data-field="status"], & .MuiDataGrid-cell[data-field="stageStatusCounts"]':
              {
                alignItems: 'flex-start',
              },
          },
        ]}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Job"
        entityName={editTarget?.buildKey ?? editTarget?.id ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        status={editTarget?.status}
        statusOptions={[
          { value: 'queued', label: formatStatusLabel('queued') },
          { value: 'running', label: formatStatusLabel('running') },
          { value: 'completed', label: formatStatusLabel('completed') },
          { value: 'failed', label: formatStatusLabel('failed') },
        ]}
        timestamp={editTargetStartedAt}
        durationMs={editTargetDurationMs}
        timestampLabel="Started"
        durationLabel="Duration"
        durationInputMode="timeParts"
        loading={editEditor.isPending}
        cascadeCopy="Tags will be applied only to this job."
        onClose={handleCloseEditDialog}
        onSave={handleSaveEditDialog}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'job' })}
        description={getDialogMessage('destructive.delete.simpleDescription', { entity: 'job' })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'job' })}
        confirmColor="red"
        loading={deleteBuildPending}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};
